import { extractPdfText } from "./extractPdfText";

export type SupportedUploadKind = "pdf" | "docx" | "txt";

export const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
] as const;

export const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".txt"] as const;

export function detectUploadKind(file: File): SupportedUploadKind | null {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (
    type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    name.endsWith(".docx")
  ) {
    return "docx";
  }
  if (type === "text/plain" || name.endsWith(".txt")) return "txt";
  return null;
}

// mammoth ships no types for the browser entry — use `any` locally and keep the
// contract at the boundary (`{ value: string }`) so callers still get types.
type MammothBrowser = {
  extractRawText(opts: { arrayBuffer: ArrayBuffer }): Promise<{ value: string }>;
};

// mammoth is ~1 MB when bundled; lazy-import so users who only ever upload PDFs
// or plain text never pull the DOCX parser into the initial chunk.
async function extractDocxText(file: File): Promise<string> {
  // mammoth has no browser-entry type declarations.
  // @ts-expect-error — see MammothBrowser interface above for the shape we rely on.
  const mammoth = (await import("mammoth/mammoth.browser")) as MammothBrowser;
  const buffer = await readArrayBuffer(file);
  const { value } = await mammoth.extractRawText({ arrayBuffer: buffer });
  return value;
}

function readArrayBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function extractTxt(file: File): Promise<string> {
  return readTextFile(file);
}

/** Cross-runtime File → string. Some jsdom versions ship a File that has
 * neither `text()` nor `arrayBuffer()`, so fall back to `FileReader` when
 * neither modern method is present. */
function readTextFile(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer().then((b) => new TextDecoder("utf-8").decode(b));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/** Route a supplier-quote-style upload to the right parser. Returns raw text.
 * Throws a plain `Error` on unsupported types so the caller can surface it. */
export async function extractFileText(file: File): Promise<string> {
  const kind = detectUploadKind(file);
  switch (kind) {
    case "pdf": return extractPdfText(file);
    case "docx": return extractDocxText(file);
    case "txt": return extractTxt(file);
    default: throw new Error("Only PDF, Word (.docx) or plain-text files are supported.");
  }
}
